/**
 * Calendar Types
 *
 * Story 3.5: Calendar Integration and Meeting Management
 *
 * Types for calendar event sync, display, and meeting scheduling.
 */
import { z } from 'zod';
import { MeetingPlatform } from './meeting-toast.js';
// Note: MeetingPlatform const and MeetingPlatformValue type are exported from meeting-toast.ts
// We use MeetingPlatformValue as the type alias throughout this file
// =============================================================================
// Enums & Constants
// =============================================================================
export const CalendarProvider = {
    GOOGLE: 'google',
    MICROSOFT: 'microsoft',
};
// Display metadata for UI. Record<CalendarProvider, ...> so a new provider is a
// compile error until it gets a label here.
export const CalendarProviderMetadata = {
    google: { label: 'Google' },
    microsoft: { label: 'Microsoft' },
};
export const CalendarSyncStatusType = {
    PENDING: 'pending',
    SYNCING: 'syncing',
    SUCCESS: 'success',
    ERROR: 'error',
};
export const AttendeeResponseStatus = {
    ACCEPTED: 'accepted',
    DECLINED: 'declined',
    TENTATIVE: 'tentative',
    NEEDS_ACTION: 'needsAction',
};
export const EventStatus = {
    CONFIRMED: 'confirmed',
    TENTATIVE: 'tentative',
    CANCELLED: 'cancelled',
};
// =============================================================================
// Zod Schemas
// =============================================================================
export const CalendarAttendeeSchema = z.object({
    email: z.string().email(),
    name: z.string().nullable(),
    responseStatus: z.enum(Object.values(AttendeeResponseStatus)),
    isOrganizer: z.boolean(),
});
export const CalendarEventSchema = z.object({
    id: z.string().uuid(),
    /** Related Revont meeting record used for in-app deep links. */
    meetingId: z.string().uuid().nullable().optional(),
    orgId: z.string(),
    userId: z.string().uuid(),
    provider: z.enum(Object.values(CalendarProvider)),
    externalId: z.string(),
    // Event details
    title: z.string(),
    description: z.string().nullable(),
    startTime: z.string().datetime({ offset: true }).or(z.string().datetime()), // ISO 8601
    endTime: z.string().datetime({ offset: true }).or(z.string().datetime()),
    durationMinutes: z.number().nullable(),
    timeZone: z.string().nullable(),
    // Location & Meeting
    location: z.string().nullable(),
    meetingLink: z.string().nullable(),
    meetingPlatform: z.enum(Object.values(MeetingPlatform)).nullable(),
    // Attendees
    attendees: z.array(CalendarAttendeeSchema),
    organizerEmail: z.string().nullable(),
    /** Whether the authenticated calendar mailbox may modify this provider event. */
    canModifyEvent: z.boolean().optional(),
    attendeeCount: z.number(),
    // Recurrence
    isRecurring: z.boolean(),
    recurrenceRule: z.string().nullable(),
    recurringEventId: z.string().nullable(),
    // True when the source event uses date-only fields (no specific time).
    // startTime/endTime are stored as UTC midnight bounds in that case;
    // consumers must render "All day" rather than formatting the clock value.
    isAllDay: z.boolean().default(false),
    // Client linking
    clientId: z.string().uuid().nullable(),
    clientName: z.string().nullable().optional(),
    clientLogoUrl: z.string().nullable().optional(),
    matchStrategy: z.enum(['email_domain', 'contact_email', 'gemini', 'title_keyword', 'manual']).nullable(),
    confidenceScore: z.number().nullable(),
    // Deal info (populated when client is linked)
    dealId: z.string().uuid().nullable().optional(),
    dealStage: z.string().nullable().optional(),
    dealName: z.string().nullable().optional(),
    // Status
    eventStatus: z.enum(Object.values(EventStatus)),
    isCancelled: z.boolean(),
    // Timestamps
    syncedAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
    createdAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
    updatedAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
});
export const CalendarSyncStateSchema = z.object({
    provider: z.enum(Object.values(CalendarProvider)),
    lastSyncAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable(),
    syncStatus: z.enum(Object.values(CalendarSyncStatusType)),
    errorMessage: z.string().nullable(),
    eventsSynced: z.number().optional(),
    eventsLinked: z.number().optional(),
});
export const CalendarSyncResponseSchema = z.object({
    success: z.boolean(),
    provider: z.enum(Object.values(CalendarProvider)),
    eventsSynced: z.number(),
    eventsLinked: z.number(),
    lastSyncAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
    error: z.string().optional(),
});
// =============================================================================
// Recurrence Types
// =============================================================================
export const RecurrenceFrequency = {
    NONE: 'none',
    DAILY: 'daily',
    WEEKLY: 'weekly',
    BIWEEKLY: 'biweekly',
    MONTHLY: 'monthly',
    CUSTOM: 'custom',
};
export const RecurrenceConfigSchema = z.object({
    frequency: z.enum(Object.values(RecurrenceFrequency)),
    interval: z.number().int().min(1).optional(),
    intervalUnit: z.enum(['days', 'weeks', 'months']).optional(),
    endCondition: z.enum(['never', 'after_count', 'on_date']),
    occurrenceCount: z.number().int().min(1).optional(),
    endDate: z.string().optional(),
});
export const CalendarAvailabilitySchema = z.enum(['busy', 'free']);
export const CalendarVisibilitySchema = z.enum(['default', 'public', 'private']);
export const CalendarGuestPermissionsSchema = z.object({
    canModifyEvent: z.boolean().optional(),
    canInviteOthers: z.boolean().optional(),
    canSeeGuestList: z.boolean().optional(),
});
export const CreateCalendarEventRequestSchema = z.object({
    title: z.string().min(1).max(500),
    startTime: z.string().datetime(), // ISO 8601
    endTime: z.string().datetime(),
    attendeeEmails: z.array(z.string().email()),
    description: z.string().optional(),
    calendarProvider: z.enum(Object.values(CalendarProvider)).optional(),
    meetingPlatform: z.enum(Object.values(MeetingPlatform)).optional(),
    location: z.string().optional(),
    isAllDay: z.boolean().optional(),
    timeZone: z.string().optional(),
    availability: CalendarAvailabilitySchema.optional(),
    visibility: CalendarVisibilitySchema.optional(),
    useDefaultReminder: z.boolean().optional(),
    reminderMinutes: z.number().int().min(0).max(40320).nullable().optional(),
    guestPermissions: CalendarGuestPermissionsSchema.optional(),
    notifyAttendees: z.boolean().optional(),
    // For follow-up meetings
    sourceMeetingId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    recurrence: RecurrenceConfigSchema.optional(),
    // Action item to link to the internal meeting created by calendar sync
    actionItemId: z.string().uuid().optional(),
});
export const CreateCalendarEventResponseSchema = z.object({
    success: z.boolean(),
    eventId: z.string(),
    externalEventId: z.string(),
    meetingLink: z.string().nullable(),
    calendarEventUrl: z.string().nullable(),
    error: z.string().optional(),
});
export const CalendarEventResponseRequestSchema = z.object({
    responseStatus: z.enum([
        AttendeeResponseStatus.ACCEPTED,
        AttendeeResponseStatus.DECLINED,
        AttendeeResponseStatus.TENTATIVE,
    ]),
    emailOrganizer: z.boolean().default(true),
    message: z.string().max(2000).optional(),
});
export const LinkCalendarEventRequestSchema = z.object({
    clientId: z.string().uuid(),
});
/**
 * Patterns for extracting meeting links from event descriptions/locations
 */
export const MEETING_LINK_PATTERNS = {
    zoom: [
        /https:\/\/zoom\.us\/j\/\d+(\?[^\s]*)*/gi,
        /https:\/\/[\w-]+\.zoom\.us\/j\/\d+(\?[^\s]*)*/gi,
    ],
    google_meet: [/https:\/\/meet\.google\.com\/[a-z]+-[a-z]+-[a-z]+/gi],
    teams: [/https:\/\/teams\.microsoft\.com\/l\/meetup-join\/[^\s]+/gi],
};
export const UpdateCalendarEventRequestSchema = z.object({
    title: z.string().min(1).optional(),
    description: z.string().optional(),
    location: z.string().optional(),
    newStartTime: z.string().datetime().optional(),
    newEndTime: z.string().datetime().optional(),
    attendeeEmails: z.array(z.string().email()).optional(),
    isAllDay: z.boolean().optional(),
    timeZone: z.string().optional(),
    availability: CalendarAvailabilitySchema.optional(),
    visibility: CalendarVisibilitySchema.optional(),
    useDefaultReminder: z.boolean().optional(),
    reminderMinutes: z.number().int().min(0).max(40320).nullable().optional(),
    guestPermissions: CalendarGuestPermissionsSchema.optional(),
    notifyAttendees: z.boolean().optional().default(true),
    recurringUpdateScope: z.enum(['this_event', 'all_events']).optional(),
});
export const UpdateCalendarEventResponseSchema = z.object({
    success: z.boolean(),
    eventId: z.string(),
    updatedFields: z.array(z.string()),
    error: z.string().optional(),
});
/**
 * Response from delete calendar event endpoint
 */
export const DeleteCalendarEventResponseSchema = z.object({
    success: z.boolean(),
    eventId: z.string(),
    error: z.string().optional(),
});
